import React from 'react';
import { FiSearch, FiUser, FiMapPin, FiStar, FiCheck, FiX } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { normalizeTruckType, getTruckTypeLabel, formatTND, formatMoverRating, formatDistanceKm } from './utils/helpers';
import { TRUCK_TYPES } from './utils/constants';

const ResultsList = ({ results, loading, onSelectMover, selectedMovers = [], onToggleSelect }) => {
  const skeletons = Array.from({ length: 4 }).map((_, i) => (
    <div key={i} className="bg-slate-800/50 p-4 rounded-xl animate-pulse flex justify-between items-center">
      <div className="space-y-2">
        <div className="h-4 bg-slate-700 rounded w-48"></div>
        <div className="h-3 bg-slate-700 rounded w-32"></div>
      </div>
      <div className="h-6 bg-slate-700 rounded w-20"></div>
    </div>
  ));

  if (loading) {
    return (
      <div className="space-y-3">
        <h2 className="text-xl font-semibold text-white px-2">Résultats</h2>
        {skeletons}
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="space-y-3">
        <h2 className="text-xl font-semibold text-white px-2">Résultats (0)</h2>
        <div className="h-full flex flex-col items-center justify-center text-center text-gray-500 bg-gray-800/50 rounded-2xl p-8 border border-gray-700">
          <FiSearch className="w-16 h-16 text-gray-600 mb-4" />
          <h3 className="text-xl font-semibold text-gray-300">Aucun déménageur trouvé</h3>
          <p className="max-w-xs mt-2">Essayez d'ajuster vos filtres ou de réessayer.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h2 className="text-xl font-semibold text-white px-2">
        Résultats ({results.length})
      </h2>
      {results.map((r) => {
        const isSelected = selectedMovers.some(m => m.id === r.id);
        return (
          <motion.button
            key={r.id}
            onClick={() => onSelectMover(r)}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.02 }}
            className={`w-full text-left bg-slate-800/50 p-4 rounded-xl border transition-colors flex justify-between items-center ${
              isSelected 
                ? 'border-amber-500 bg-amber-500/10' 
                : 'border-slate-700 hover:border-amber-500/50'
            }`}
          >
          <div className="flex items-center gap-4 flex-1">
            {/* Bouton de sélection pour comparaison */}
            {onToggleSelect && (
              <span
                role="checkbox"
                aria-checked={isSelected}
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSelect(r);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onToggleSelect(r);
                  }
                }}
                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0 cursor-pointer ${
                  isSelected
                    ? 'bg-amber-500 border-amber-500'
                    : 'bg-gray-700 border-gray-600 hover:border-amber-500'
                }`}
              >
                {isSelected && <FiCheck className="w-4 h-4 text-white" />}
              </span>
            )}
            <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center flex-shrink-0 border border-slate-600">
              <FiUser className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h3 className="font-bold text-white">
                {r.firstName} {r.lastName}
              </h3>
              <div className="flex items-center gap-3 text-sm text-gray-400 mt-1">
                <span className="flex items-center gap-1">
                  <FiMapPin className="inline" />
                  {formatDistanceKm(r.distance)} km
                </span>
                <span className="flex items-center gap-1">
                  <FiStar className="inline fill-yellow-400 text-yellow-400" />
                  {formatMoverRating(r.rating, r.reviewCount ?? r.reviews)}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-2">
                {r.truckType && (
                  <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-300 rounded-full border border-amber-500/30">
                    {getTruckTypeLabel(r.truckType)}
                  </span>
                )}
                {r.availableTrucks && r.availableTrucks.length > 0 && (
                  <span className="text-xs text-gray-400">
                    +{r.availableTrucks.length} autres
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-semibold text-amber-400">
              ≈ {formatTND(r.price || 0)}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              {TRUCK_TYPES[normalizeTruckType(r.truckType)]?.volume || '?'} m³ • 
              {TRUCK_TYPES[normalizeTruckType(r.truckType)]?.teamSize || '?'} pers.
            </div>
          </div>
        </motion.button>
        );
      })}
    </div>
  );
};

export default ResultsList;




